import React from "react";
import cx from "classnames";
import DesignChooserConstants from "../../constants/DesignChooserConstants";
import DesignChooserActions from "../../actions/DesignChooserActions";
import Navigator from "./Navigator";
import PreviewFooter from "./PreviewFooter";
import ThemeChooser from "./ThemeChooser";
import PreviewStage from "./PreviewStage";
import SidePanel from "../SidePanel";
import { recursivelyCopySyncedPropsToMobile } from "../../utils/SyncUtils";
import uiPrefix from "../../utils/UiPrefixUtils";

const { TAB_CURRENT_SITE, TAB_USER_SITES, TAB_USER_LIBRARY, TAB_PLATFORM_LIBRARY } =
  DesignChooserConstants;

const _getTabs = ({ tab, _selectTab, demoMode, showTabs }) => {
  const tabStyle = {
    width: "auto",
    padding: "8px 16px",
    flexGrow: 0,
  };

  const currentSiteTabClass = cx({
    tab: true,
    [uiPrefix("current-site-tab")]: true,
    active: tab === TAB_CURRENT_SITE,
  });

  const currentSiteTab = (
    <div
      data-e2e={uiPrefix("current-site-tab")}
      className={currentSiteTabClass}
      style={tabStyle}
      onClick={() => _selectTab(TAB_CURRENT_SITE)}
    >
      Current Site
    </div>
  );

  const mySitesTabClass = cx({
    tab: true,
    active: tab === TAB_USER_SITES,
  });
  const mySitesTab = (
    <div className={mySitesTabClass} style={tabStyle} onClick={() => _selectTab(TAB_USER_SITES)}>
      My Sites
    </div>
  );

  const myLibTabClass = cx({
    tab: true,
    active: tab === TAB_USER_LIBRARY,
  });
  const myLibTab = (
    <div className={myLibTabClass} style={tabStyle} onClick={() => _selectTab(TAB_USER_LIBRARY)}>
      My Library
    </div>
  );

  let showitLibTab = null;
  if (!demoMode) {
    const showitLibTabClass = cx({
      tab: true,
      active: tab === TAB_PLATFORM_LIBRARY,
    });
    showitLibTab = (
      <div
        className={showitLibTabClass}
        style={tabStyle}
        onClick={() => _selectTab(TAB_PLATFORM_LIBRARY)}
      >
        Showit Library
      </div>
    );
  }

  if (showTabs) {
    return (
      <div className="properties-tabset" style={{ margin: 0 }}>
        {currentSiteTab}
        {mySitesTab}
        {myLibTab}
        {showitLibTab}
      </div>
    );
  }
  return null;
};

const DesignChooser = ({
  tab,
  showTabs,
  demoMode,
  actionEnabled,
  designList,
  listOpen,
  preview,
  previewLayout,
  useCurrentTheme,
  restoreMode,
  actionLabel,
}) => {
  const _selectTab = (passedTab) => {
    DesignChooserActions.selectTab({ tab: passedTab });
  };

  const _selectItem = (item) => {
    DesignChooserActions.selectItem({ item });
  };

  const _selectLayout = (layout) => {
    DesignChooserActions.selectLayout(layout);
  };

  const _chooseItem = () => {
    DesignChooserActions.chooseItem();
  };

  const _toggleLeftPanel = () => {
    DesignChooserActions.toggleList();
  };

  const _toggleTheme = (value) => {
    DesignChooserActions.toggleTheme(value);
  };

  const viewAreaStyle = {
    display: "flex",
    minHeight: 0,
    justifyContent: "flex-start",
    alignItems: "stretch",
    alignContent: "stretch",
    flex: "1 1 auto",
    position: "relative",
    height: "100%",
  };

  const previewStyle = {
    display: "flex",
    minHeight: 0,
    justifyContent: "flex-start",
    alignItems: "stretch",
    alignContent: "stretch",
    flex: "1 1 auto",
    position: "relative",
    flexDirection: "column",
    minWidth: 0,
  };

  const themeChooser =
    tab !== TAB_CURRENT_SITE ? (
      <ThemeChooser useCurrentTheme={useCurrentTheme} onThemeSelect={_toggleTheme} />
    ) : null;

  const [theme] = recursivelyCopySyncedPropsToMobile(
    !useCurrentTheme && preview ? preview.theme : designList.currentTheme
  );

  const calculatedActionLabel = restoreMode
    ? `RESTORE ${designList.selected.readableType}`
    : actionLabel;

  return (
    <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="properties-tabset-wrapper">
        {_getTabs({ tab, _selectTab, demoMode, showTabs })}
      </div>
      <div style={viewAreaStyle}>
        <SidePanel key="navPanel" position="left" toggle={_toggleLeftPanel} open={listOpen}>
          {designList.error && <div style={{ margin: ".5rem 1rem" }}>{designList.error}</div>}
          <Navigator list={designList} onSelectItem={_selectItem} />
        </SidePanel>
        <div style={previewStyle}>
          <PreviewStage
            theme={theme}
            preview={preview}
            layout={previewLayout}
            useCurrentTheme={useCurrentTheme}
          />
          <PreviewFooter
            layout={previewLayout}
            actionLabel={calculatedActionLabel}
            actionEnabled={actionEnabled}
            onActionClick={_chooseItem}
            onSelectLayout={_selectLayout}
          >
            {themeChooser}
          </PreviewFooter>
        </div>
      </div>
    </div>
  );
};

export default DesignChooser;
